import { view, hdrm } from '@yola/ws-sdk';
import buildPrefixedClassToken from '../../breakpoint-editing/helpers/build-prefixed-class-token';
import { breakpointPrefixByKey } from '../constants/breakpoints';
import normalizeResponsiveClasses from './normalize-responsive-classes';
import { serializeClassToken, patchClass, removeClass } from './handle-class-token';

const { UPDATE_LIVE_ONLY, UPDATE_BOTH } = hdrm.constants.updateStrategies;

const buildResponsiveClassName = (target, value, breakpointKey) => {
  const token = serializeClassToken(target, value);

  return buildPrefixedClassToken(token, breakpointPrefixByKey[breakpointKey]);
};

const serializeClassTargetsByElementId = (classState = {}) =>
  Object.keys(classState).reduce((result, elementId) => {
    const targetsByKey = classState[elementId];
    const targets = Object.keys(targetsByKey);
    const tokens = [];

    targets.forEach((className) => {
      const breakpointBasedValue = targetsByKey[className];

      Object.keys(breakpointBasedValue).forEach((breakpointKey) => {
        const breakpointValue = breakpointBasedValue[breakpointKey];

        if (breakpointValue === undefined) return;

        tokens.push(buildResponsiveClassName(className, breakpointValue, breakpointKey));
      });
    });

    return {
      ...result,
      [elementId]: {
        targets,
        newClass: tokens.join(' '),
      },
    };
  }, {});

const getLivePreviewActions = (classState, options) =>
  Object.keys(classState).map((elementId) => {
    const targetsByKey = classState[elementId];
    const element = view.accessors.getLiveElement(elementId);
    let currentClass = element?.getAttribute('class') || '';

    Object.keys(targetsByKey).forEach((target) => {
      const breakpointBasedValue = targetsByKey[target];

      Object.keys(breakpointBasedValue).forEach((breakpointKey) => {
        const breakpointValue = breakpointBasedValue[breakpointKey];

        if (breakpointValue === undefined) {
          currentClass = removeClass({
            currentClass,
            target,
            breakpointKey,
          });
        } else {
          currentClass = patchClass({
            currentClass,
            target,
            value: breakpointValue,
            breakpointKey,
          });
        }
      });
    });

    return view.actions.setElementAttribute(elementId, 'class', currentClass, options);
  });

const getSubmissionActions = (classState, options) => {
  const serializedByElementId = serializeClassTargetsByElementId(classState);

  return Object.keys(serializedByElementId).map((elementId) => {
    const { targets, newClass } = serializedByElementId[elementId];
    const element = view.accessors.getStaticElement(elementId);
    const currentClass = element?.getAttribute('class') || '';
    const finalClass = normalizeResponsiveClasses({ targets, currentClass, newClass });

    return view.actions.setElementAttribute(elementId, 'class', finalClass, options);
  });
};

const getClassActionsToApply = (state = {}, options = {}) => {
  if (!Object.keys(state).length) {
    return [];
  }

  const { strategy } = options;

  if (strategy === UPDATE_LIVE_ONLY) {
    return getLivePreviewActions(state, options);
  }

  if (strategy === UPDATE_BOTH) {
    return getSubmissionActions(state, options);
  }

  return [];
};

export default getClassActionsToApply;
